import { Close } from '@coconut-software/ui/dist/icons';
import clsx from 'clsx';
import type { FC } from 'react';
import React from 'react';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';
import { createUseStyles, useTheme } from 'react-jss';
import { RemoveScroll } from 'react-remove-scroll';
import {
  EnhancedKioskLanguageRadioGroup,
  Orders,
} from './EnhancedKioskLanguageRadioGroup';

const useStyles = createUseStyles((theme: any) => ({
  closeButton: {
    '& svg': {
      width: '3.0rem',
      height: '3.0rem',
    },
    '&:focus': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.contrastText}`,
    },
    '&:active': {
      backgroundColor: 'var(--color-primary-darker)',
    },
  },
  dialogBg: {
    backgroundColor: 'var(--color-primary-main)',
  },
}));

interface EnhancedKioskLanguageDialogProps {
  open?: boolean;
  onDismiss?: () => void;
}

const EnhancedKioskLanguageDialog: FC<EnhancedKioskLanguageDialogProps> = ({
  onDismiss,
  open = false,
}) => {
  const intl = useIntl();
  const classes = useStyles({ theme: useTheme() });

  if (!open) {
    return null;
  }

  const handleDismiss = () => {
    onDismiss?.();
  };

  // Optimization - ReactFocusLock runs very slowly in test environments.
  // We set this explicitly in Dialog.tests, but consumers can
  // set it explicitly or fallback to true in 'test' states.
  const disabled = Object.hasOwnProperty.call(
    process.env,
    'DISABLE_SCROLL_FOCUS_LOCK',
  )
    ? process.env.DISABLE_SCROLL_FOCUS_LOCK === 'true'
    : process.env.NODE_ENV === 'test';

  return (
    <RemoveScroll enabled={!disabled}>
      <FocusLock returnFocus>
        <div className="absolute top-0 left-0 w-full h-full z-10">
          <div
            className={clsx(
              'absolute top-0 left-0 w-full h-full opacity-95',
              classes.dialogBg,
            )}
          />
          <div className="relative h-full flex flex-col p-8">
            <button
              aria-label={intl.formatMessage({ id: 'Ui.close' })}
              className={clsx(
                'ml-auto rounded-full focus:outline-none',
                classes.closeButton,
              )}
              onClick={handleDismiss}
              type="button"
            >
              <Close />
            </button>
            <div className="flex flex-col items-center justify-center h-full">
              <EnhancedKioskLanguageRadioGroup
                displayIcon
                onChange={handleDismiss}
                order={Orders.Column}
                variant="solid"
              />
            </div>
          </div>
        </div>
      </FocusLock>
    </RemoveScroll>
  );
};

export { EnhancedKioskLanguageDialog };
